import React, { useState, useEffect } from 'react';
import './MyServers.css';

function MyServers({ authToken, onTokenExpiration, onServerManage }) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restoringServers, setRestoringServers] = useState(new Set());
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [serverName, setServerName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const fetchServerData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('https://api.vmp.ir/dev/api.php?work=getData', {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        onTokenExpiration();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.licenses) {
        setServers(data.licenses);
      } else {
        throw new Error('Invalid server data received');
      }
    } catch (error) {
      console.error('Failed to fetch server data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) {
      fetchServerData();
    }
  }, [authToken]);

  const handleRestoreServer = async (server) => {
    try {
      setRestoringServers(prev => new Set(prev).add(server.id_2));
      setError(null);
      
      const response = await fetch(`https://api.vmp.ir/dev/api.php?work=trashLicense&license=${server.license}`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        onTokenExpiration();
        return;
      }

      if (response.status === 401) {
        setError('Cannot restore server: Active server limit exceeded. Please trash some active servers first.');
        return;
      }

      if (response.status === 200) {
        // Successfully restored, refresh the server list
        await fetchServerData();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to restore server:', error);
      setError(`Failed to restore server: ${error.message}`);
    } finally {
      setRestoringServers(prev => {
        const newSet = new Set(prev);
        newSet.delete(server.id_2);
        return newSet;
      });
    }
  };

  const handleCreateServer = async () => {
    if (!serverName.trim()) {
      setError('Server name is required');
      return;
    }

    if (serverName.length > 100) {
      setError('Server name must be 100 characters or less');
      return;
    }

    try {
      setIsCreating(true);
      setError(null);
      
      const response = await fetch(`https://api.vmp.ir/dev/api.php?work=createLicense&name=${encodeURIComponent(serverName.trim())}`, {
        method: 'GET',
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        onTokenExpiration();
        return;
      }

      if (response.status === 401) {
        setError('Maximum number of licenses has been reached. Please trash some existing servers first.');
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Successfully created, refresh the server list and close dialog
      await fetchServerData();
      setShowCreateDialog(false);
      setServerName('');
    } catch (error) {
      console.error('Failed to create server:', error);
      setError(`Failed to create server: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateClick = () => {
    setShowCreateDialog(true);
    setServerName('');
    setError(null);
  };

  const handleCancelCreate = () => {
    setShowCreateDialog(false);
    setServerName('');
    setError(null);
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    if (name.length <= 100) {
      setServerName(name);
    }
  };

  const formatLastStart = (timestamp) => {
    if (!timestamp || timestamp === 0) {
      return 'Never started';
    }
    
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  const getServerStatus = (online, trash, lastStart) => {
    if (trash === 1) return 'trashed';
    if (online === 1) return 'online';
    if (online === 0 && lastStart > 0) return 'offline';
    if (!lastStart || lastStart === 0) return 'never_started';
    return 'offline';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return '#10b981';
      case 'offline':
        return '#ef4444';
      case 'trashed':
        return '#6b7280';
      case 'never_started':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return '●';
      case 'offline':
        return '●';
      case 'trashed':
        return '🗑';
      case 'never_started':
        return '⏳';
      default:
        return '●';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online':
        return 'Online';
      case 'offline':
        return 'Offline';
      case 'trashed':
        return 'Trashed';
      case 'never_started':
        return 'Not Started';
      default:
        return 'Unknown';
    }
  };

  const getStatusPriority = (status) => {
    switch (status) {
      case 'online':
        return 1;
      case 'offline':
        return 2;
      case 'never_started':
        return 3;
      case 'trashed':
        return 4;
      default:
        return 5;
    }
  };

  const sortServersByStatus = (servers) => {
    return [...servers].sort((a, b) => {
      const statusA = getServerStatus(a.online, a.trash, a.last_start);
      const statusB = getServerStatus(b.online, b.trash, b.last_start);
      const priorityA = getStatusPriority(statusA);
      const priorityB = getStatusPriority(statusB);
      
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      
      // If same status, sort by label/name
      const nameA = a.label === 'NO NAME' ? `Server ${a.id_2}` : a.label;
      const nameB = b.label === 'NO NAME' ? `Server ${b.id_2}` : b.label;
      return nameA.localeCompare(nameB);
    });
  };

  if (loading) {
    return (
      <div className="my-servers">
        <div className="servers-header">
          <h3>My Servers</h3>
          <p>Loading your server data...</p>
        </div>
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-servers">
        <div className="servers-header">
          <h3>My Servers</h3>
          <p>Error loading server data</p>
        </div>
        <div className="error-message">
          <p>Error: {error}</p>
          <button onClick={fetchServerData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const sortedServers = sortServersByStatus(servers);

  return (
    <div className="my-servers">
      <div className="servers-header">
        <h3>My Servers</h3>
        <p>Manage and monitor your VMP servers ({servers.length} total)</p>
      </div>
      
      <div className="servers-grid">
        {sortedServers.map((server) => {
          const status = getServerStatus(server.online, server.trash, server.last_start);
          const displayName = server.label === 'NO NAME' ? `Server ${server.id_2}` : server.label;
          
          return (
            <div key={server.id_2} className="server-card">
              <div className="server-header">
                <div className="server-name">
                  <h4>{displayName}</h4>
                  <div className="server-status">
                    <span 
                      className="status-indicator"
                      style={{ color: getStatusColor(status) }}
                    >
                      {getStatusIcon(status)}
                    </span>
                    <span className="status-text">{getStatusText(status)}</span>
                  </div>
                </div>
              </div>
              
              <div className="server-details">
                <div className="detail-item">
                  <span className="detail-label">Server ID:</span>
                  <span className="detail-value">{server.id_2}</span>
                </div>
                {server.vanity_id && (
                  <div className="detail-item">
                    <span className="detail-label">Vanity ID:</span>
                    <span className="detail-value">
                      {server.vanity_id}{server.vanity_active ? '' : ' (inactive)'}
                    </span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Last Started:</span>
                  <span className="detail-value">{formatLastStart(server.last_start)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">IP Address:</span>
                  <span className="detail-value">{server.ip || 'Not assigned'}</span>
                </div>
              </div>
              
              <div className="server-footer">
                {status === 'trashed' ? (
                  <button 
                    className="restore-button"
                    onClick={() => handleRestoreServer(server)}
                    disabled={restoringServers.has(server.id_2)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                      <path d="M21 3v5h-5"/>
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                      <path d="M3 21v-5h5"/>
                    </svg>
                    {restoringServers.has(server.id_2) ? 'Restoring...' : 'Restore'}
                  </button>
                ) : (
                  <button 
                    className="manage-button" 
                    onClick={() => onServerManage && onServerManage(server)}
                  >
                    Manage Server
                  </button>
                )}
              </div>
            </div>
          );
        })}
        
        <div className="add-server-card">
          <div className="add-server-content">
            <div className="add-server-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </div>
            <h4>Add New Server</h4>
            <p>Deploy a new VMP server instance</p>
            <button className="add-button" onClick={handleCreateClick}>
              Create Server
            </button>
          </div>
        </div>
      </div>

      {showCreateDialog && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <div className="confirmation-header">
              <h3>Create New Server</h3>
            </div>
            <div className="confirmation-content">
              <p>Enter a name for your new server (maximum 100 characters)</p>
              <div className="name-input-container">
                <input
                  type="text"
                  className="server-name-input"
                  placeholder="Server name..."
                  value={serverName}
                  onChange={handleNameChange}
                  maxLength={100}
                  autoFocus
                />
                <div className="character-count">
                  {serverName.length}/100
                </div>
              </div>
              {error && (
                <div className="error-message">
                  <p>{error}</p>
                </div>
              )}
            </div>
            <div className="confirmation-actions">
              <button 
                className="action-btn cancel-btn"
                onClick={handleCancelCreate}
                disabled={isCreating}
              >
                Cancel
              </button>
              <button 
                className="action-btn confirm-btn"
                onClick={handleCreateServer}
                disabled={isCreating || !serverName.trim()}
              >
                {isCreating ? 'Creating...' : 'Create Server'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyServers;